import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, ScrollText, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { exportInvKardex } from "~/services/inventory/exportService";
import { inventoryOperationsService } from "~/services/inventory/operationsService";
import { formatDate } from "~/utils/helpers";

const LOCATION_OPTIONS = [
  { value: "all", label: "Todas las plantas" },
  { value: "P1/STOCK", label: "Planta 1" },
  { value: "P2/STOCK", label: "Planta 2" },
  { value: "NC", label: "No Conforme" },
];

const formatQty = (n: number) =>
  n ? new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n) : "";

export function InventoryKardexPage() {
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("all");

  const { data, isLoading } = useQuery({
    queryKey: ["inv-kardex", search, location],
    queryFn: () =>
      inventoryOperationsService.getKardex({
        search,
        locationCode: location === "all" ? null : location,
      }),
  });
  const rows = data ?? [];

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. Saldo
          corrido por producto y planta a partir del libro de movimientos.
        </span>
      </div>

      <PageHeader
        title="Inventario — Historial de movimientos"
        description="Entradas, salidas y saldo corrido por producto y ubicación."
        actions={
          <Button
            variant="outline"
            className="h-9"
            disabled={rows.length === 0}
            onClick={() => exportInvKardex(rows)}
          >
            <Download className="mr-1.5 h-4 w-4" />
            Exportar Excel
          </Button>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre…"
            className="h-9 pl-9"
          />
        </div>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="h-9 w-full sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LOCATION_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton rows={10} columns={8} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="Sin movimientos"
          description="No hay movimientos para el filtro seleccionado."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Ubicación</TableHead>
                    <TableHead>Transacción</TableHead>
                    <TableHead className="text-right">Entrada</TableHead>
                    <TableHead className="text-right">Salida</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.documento ?? "op"}-${r.producto}-${r.ubicacion}-${i}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDate(r.fecha)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {r.documento ?? "-"}
                      </TableCell>
                      <TableCell className="font-medium">
                        {r.code ? <span className="text-muted-foreground">{r.code} · </span> : null}
                        {r.producto}
                      </TableCell>
                      <TableCell>{r.ubicacion}</TableCell>
                      <TableCell className="text-muted-foreground">{r.tipo ?? "-"}</TableCell>
                      <TableCell className="text-right tabular-nums text-green-700">
                        {formatQty(r.entrada)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-red-700">
                        {formatQty(r.salida)}
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums">
                        {new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(r.saldo)}{" "}
                        {r.uom ?? ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default InventoryKardexPage;
